import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { mockData } from '../mock';
import ChartCard from '../components/ChartCard';
import PeriodFilter from '../components/PeriodFilter';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Zap, Sun, CarFront, Droplets, MoveVertical, Wifi, RefreshCw, AlertTriangle, CheckCircle2, Wrench } from 'lucide-react';
import { subDays, isAfter, isBefore, parseISO, format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cores para dispositivos de água
const WATER_DEVICE_COLORS = ['#06b6d4', '#0891b2', '#0e7490', '#155e75', '#164e63'];

// Função para formatar números com vírgula como separador decimal
const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return num.toFixed(decimals).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Função para formatar valores para tooltip
const formatTooltipValue = (value, decimals = 2) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return num.toFixed(decimals).replace('.', ',');
};

const StatCard = ({ title, value, unit, icon: Icon, color, isRealData, subValue }) => (
  <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all border border-gray-100">
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {isRealData && (
            <Badge className="bg-green-100 text-green-700 text-xs px-1 py-0">
              <Wifi className="h-2 w-2 mr-0.5" />
              Real
            </Badge>
          )}
        </div>
        <p className="text-3xl font-bold text-gray-900">
          {value}
          <span className="text-lg text-gray-500 ml-2">{unit}</span>
        </p>
        {subValue && (
          <p className="text-xs text-gray-500 mt-1">{subValue}</p>
        )}
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [periodFilter, setPeriodFilter] = useState({ type: 'preset', days: 180 });
  const [loading, setLoading] = useState(false);
  const [waterApiConnected, setWaterApiConnected] = useState(false);
  const [waterRealData, setWaterRealData] = useState(null);
  const [waterConsumosData, setWaterConsumosData] = useState({});
  const [deviceConfigs, setDeviceConfigs] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  // Novos estados — dados reais Energia / Fotovoltaico / Elevadores
  const [energiaKpis, setEnergiaKpis] = useState(null);
  const [energiaSerie, setEnergiaSerie] = useState([]);
  const [huaweiSummary, setHuaweiSummary] = useState(null);
  const [elevadoresSummary, setElevadoresSummary] = useState(null);

  // Construtor do query string com from/to baseado no filtro de período
  const periodQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (periodFilter.type === 'preset' && periodFilter.days) {
      const to = new Date();
      const from = subDays(to, periodFilter.days);
      params.set('from', from.toISOString().slice(0, 19));
      params.set('to', to.toISOString().slice(0, 19));
    } else if (periodFilter.type === 'custom' && periodFilter.startDate && periodFilter.endDate) {
      params.set('from', periodFilter.startDate.toISOString().slice(0, 19));
      params.set('to', periodFilter.endDate.toISOString().slice(0, 19));
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [periodFilter]);

  // Carregar dados reais da água
  const loadWaterData = useCallback(async () => {
    setLoading(true);
    try {
      const testResponse = await fetch(`${API_URL}/api/agua/test-connection`);
      const testData = await testResponse.json();
      setWaterApiConnected(testData.success);

      if (testData.success) {
        const configResponse = await fetch(`${API_URL}/api/configuracoes/dispositivos?categoria=agua`);
        const configs = await configResponse.json();
        setDeviceConfigs(configs || []);

        const resumoResponse = await fetch(`${API_URL}/api/agua/resumo`);
        const resumoData = await resumoResponse.json();
        
        if (resumoData.success) {
          setWaterRealData(resumoData);
          setLastUpdate(new Date());
        }

        const dias = Math.min(periodFilter.type === 'preset' ? periodFilter.days : 30, 30);
        
        const consumosPromises = (configs || []).map(async (config) => {
          const response = await fetch(`${API_URL}/api/agua/consumos/${config.device_id}?dias=${dias}`);
          const data = await response.json();
          return { deviceId: config.device_id, ...data };
        });

        const consumosResults = await Promise.all(consumosPromises);
        const consumosMap = {};
        consumosResults.forEach(result => {
          consumosMap[result.deviceId] = result;
        });
        setWaterConsumosData(consumosMap);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de água:', error);
      setWaterApiConnected(false);
    }
    setLoading(false);
  }, [periodFilter]);

  // Loader dos novos módulos com dados reais
  const loadOtherRealData = useCallback(async () => {
    try {
      const [kpiRes, serieRes, huaweiRes, elevRes] = await Promise.all([
        fetch(`${API_URL}/api/energia/kpis${periodQuery}`),
        fetch(`${API_URL}/api/energia/serie${periodQuery}${periodQuery ? '&' : '?'}granularity=day`),
        fetch(`${API_URL}/api/huawei/summary`),
        fetch(`${API_URL}/api/elevadores/summary`),
      ]);
      if (kpiRes.ok) setEnergiaKpis(await kpiRes.json());
      if (serieRes.ok) setEnergiaSerie(await serieRes.json());
      if (huaweiRes.ok) setHuaweiSummary(await huaweiRes.json());
      if (elevRes.ok) setElevadoresSummary(await elevRes.json());
    } catch (err) {
      console.error('Erro ao carregar dados reais (Energia/Huawei/Elevadores):', err);
    }
  }, [periodQuery]);

  useEffect(() => {
    loadWaterData();
    loadOtherRealData();
  }, [loadWaterData, loadOtherRealData]);

  // Calcular consumo de água do período (dados reais)
  const waterPeriodConsumption = useMemo(() => {
    if (!waterApiConnected || Object.keys(waterConsumosData).length === 0) {
      return null;
    }

    let totalLitros = 0;
    Object.values(waterConsumosData).forEach(deviceData => {
      if (deviceData.consumos) {
        totalLitros += deviceData.consumos.reduce((sum, c) => sum + (c.valor_litros || 0), 0);
      }
    });

    return (totalLitros / 1000); // m³
  }, [waterApiConnected, waterConsumosData]);

  // Dados diários de água agregados (para gráficos)
  const waterDailyData = useMemo(() => {
    if (!waterApiConnected || Object.keys(waterConsumosData).length === 0) {
      return [];
    }

    const dailyTotals = {};
    
    Object.entries(waterConsumosData).forEach(([deviceId, deviceData]) => {
      if (deviceData.consumos) {
        deviceData.consumos.forEach(consumo => {
          const date = consumo.timestamp.split('T')[0];
          if (!dailyTotals[date]) {
            dailyTotals[date] = 0;
          }
          dailyTotals[date] += (consumo.valor_litros || 0) / 1000;
        });
      }
    });

    return Object.entries(dailyTotals)
      .map(([date, total]) => ({
        data: date,
        agua: parseFloat(total.toFixed(3))
      }))
      .sort((a, b) => a.data.localeCompare(b.data));
  }, [waterApiConnected, waterConsumosData]);

  // Dados mensais de água por dispositivo (para gráfico stacked)
  const waterMonthlyByDevice = useMemo(() => {
    if (!waterApiConnected || Object.keys(waterConsumosData).length === 0) {
      return {};
    }

    const monthlyByDevice = {};
    
    Object.entries(waterConsumosData).forEach(([deviceId, deviceData]) => {
      const config = deviceConfigs.find(c => c.device_id === deviceId);
      const deviceName = config?.descricao || deviceId;
      
      if (deviceData.consumos) {
        deviceData.consumos.forEach(consumo => {
          const date = new Date(consumo.timestamp);
          // Usar formato sem locale para coincidir com mockData.mensal
          const monthKey = format(date, 'MMM yyyy');
          
          if (!monthlyByDevice[monthKey]) {
            monthlyByDevice[monthKey] = {};
          }
          if (!monthlyByDevice[monthKey][deviceName]) {
            monthlyByDevice[monthKey][deviceName] = 0;
          }
          monthlyByDevice[monthKey][deviceName] += (consumo.valor_litros || 0) / 1000;
        });
      }
    });

    return monthlyByDevice;
  }, [waterApiConnected, waterConsumosData, deviceConfigs]);

  // Dados mensais de água agregados (para gráficos mensais)
  const waterMonthlyData = useMemo(() => {
    if (!waterApiConnected || waterDailyData.length === 0) {
      return {};
    }

    const monthlyTotals = {};
    
    waterDailyData.forEach(day => {
      const date = new Date(day.data);
      const monthKey = format(date, 'MMM yyyy');
      
      if (!monthlyTotals[monthKey]) {
        monthlyTotals[monthKey] = 0;
      }
      monthlyTotals[monthKey] += day.agua;
    });

    return monthlyTotals;
  }, [waterApiConnected, waterDailyData]);

  // Nomes dos dispositivos para o gráfico stacked
  const deviceNames = useMemo(() => {
    return deviceConfigs.map(c => c.descricao || c.device_id);
  }, [deviceConfigs]);

  // Energia mensal real (agregada da serie diária)
  const energiaMonthlyData = useMemo(() => {
    if (!energiaSerie || energiaSerie.length === 0) return {};
    const monthly = {};
    energiaSerie.forEach((row) => {
      try {
        const d = parseISO(row.ts);
        const monthKey = format(d, 'MMM yyyy');
        monthly[monthKey] = (monthly[monthKey] || 0) + (row.consumo_kwh || 0);
      } catch { /* ignore */ }
    });
    return monthly;
  }, [energiaSerie]);

  // Total Energia real no período (a partir dos KPIs do backend)
  const energiaPeriodTotal = useMemo(() => {
    return energiaKpis?.consumo_total_kwh ?? null;
  }, [energiaKpis]);

  const filteredMonthlyData = useMemo(() => {
    let data;
    if (periodFilter.type === 'preset') {
      const months = Math.ceil(periodFilter.days / 30);
      data = mockData.mensal.slice(-months);
    } else {
      data = mockData.mensal.filter(item => {
        const itemDate = new Date(item.mes);
        return isAfter(itemDate, periodFilter.startDate) && isBefore(itemDate, periodFilter.endDate);
      });
    }

    // Substituir valores mensais por dados reais quando disponíveis (Água + Energia)
    return data.map(item => {
      const realWaterValue = waterMonthlyData[item.mes];
      const deviceValues = waterMonthlyByDevice[item.mes] || {};
      const realEnergiaValue = energiaMonthlyData[item.mes];

      return {
        ...item,
        agua: realWaterValue !== undefined ? parseFloat(realWaterValue.toFixed(3)) : item.agua,
        aguaReal: realWaterValue !== undefined,
        energia: realEnergiaValue !== undefined ? Math.round(realEnergiaValue) : item.energia,
        energiaReal: realEnergiaValue !== undefined,
        ...deviceValues
      };
    });
  }, [periodFilter, waterApiConnected, waterMonthlyData, waterMonthlyByDevice, energiaMonthlyData]);

  // Dados mensais apenas de água com dispositivos (para gráfico stacked)
  const waterStackedChartData = useMemo(() => {
    if (!waterApiConnected || Object.keys(waterMonthlyByDevice).length === 0) {
      return [];
    }

    // Converter objeto para array ordenado por mês
    return Object.entries(waterMonthlyByDevice)
      .map(([mes, dispositivos]) => ({
        mes,
        ...dispositivos
      }))
      .sort((a, b) => {
        const dateA = new Date(a.mes);
        const dateB = new Date(b.mes);
        return dateA - dateB;
      });
  }, [waterApiConnected, waterMonthlyByDevice]);

  const combinedDailyData = useMemo(() => {
    if (!waterApiConnected || waterDailyData.length === 0) {
      return null;
    }
    return waterDailyData;
  }, [waterApiConnected, waterDailyData]);

  // Dados combinados para a Visão Geral (mesma estrutura, agora com Energia real quando disponível)
  const combinedAllCategoriesData = useMemo(() => {
    if (!waterApiConnected || Object.keys(waterMonthlyData).length === 0) {
      return [];
    }

    const realMonths = Object.keys(waterMonthlyData);

    return realMonths.map(mes => {
      const staticData = mockData.mensalEstatico?.find(d => d.mes === mes) || {
        energia: Math.floor(Math.random() * (15000 - 10000) + 10000),
        fotovoltaico: Math.floor(Math.random() * (8000 - 5000) + 5000),
        carregamento: Math.floor(Math.random() * (3000 - 1500) + 1500),
        elevadores: Math.floor(Math.random() * (10000 - 6000) + 6000)
      };

      const aguaReal = waterMonthlyData[mes] || 0;
      const energiaReal = energiaMonthlyData[mes];

      return {
        mes,
        energia: energiaReal !== undefined ? Math.round(energiaReal) : staticData.energia,
        energiaReal: energiaReal !== undefined,
        fotovoltaico: staticData.fotovoltaico,
        carregamento: staticData.carregamento,
        agua: parseFloat(aguaReal.toFixed(2)),
        elevadores: staticData.elevadores,
        aguaReal: true
      };
    }).sort((a, b) => {
      const dateA = new Date(a.mes);
      const dateB = new Date(b.mes);
      return dateA - dateB;
    });
  }, [waterApiConnected, waterMonthlyData, energiaMonthlyData]);

  const totalStats = useMemo(() => {
    const dataToUse = combinedAllCategoriesData.length > 0 ? combinedAllCategoriesData : filteredMonthlyData;
    const totals = dataToUse.reduce((acc, month) => {
      return {
        energia: acc.energia + month.energia,
        fotovoltaico: acc.fotovoltaico + month.fotovoltaico,
        carregamento: acc.carregamento + month.carregamento,
        agua: acc.agua + month.agua,
        elevadores: acc.elevadores + month.elevadores
      };
    }, {
      energia: 0,
      fotovoltaico: 0,
      carregamento: 0,
      agua: 0,
      elevadores: 0
    });
    
    return totals;
  }, [filteredMonthlyData, combinedAllCategoriesData]);

  const hasRealWaterData = useMemo(() => {
    return filteredMonthlyData.some(item => item.aguaReal);
  }, [filteredMonthlyData]);

  // Custom Tooltip para formato português
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatTooltipValue(entry.value)} {entry.name.includes('m³') ? '' : entry.name.includes('kWh') ? '' : entry.name.includes('min') ? '' : ''}
              {entry.payload?.aguaReal && entry.name.includes('Água') ? ' (Real)' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-gray-600">Visão geral da plataforma de gestão técnica</p>
            {waterApiConnected && (
              <Badge className="bg-green-100 text-green-800">
                <Wifi className="h-3 w-3 mr-1" />
                Água: API Conectada
              </Badge>
            )}
            {energiaKpis && energiaKpis.locais > 0 && (
              <Badge className="bg-green-100 text-green-800">
                <Wifi className="h-3 w-3 mr-1" />
                Energia: {energiaKpis.locais} locais
              </Badge>
            )}
            {huaweiSummary && huaweiSummary.stations_count > 0 && (
              <Badge className="bg-green-100 text-green-800">
                <Wifi className="h-3 w-3 mr-1" />
                Fotovoltaico: {huaweiSummary.stations_online}/{huaweiSummary.stations_count} online
              </Badge>
            )}
            {elevadoresSummary && elevadoresSummary.total_elevadores > 0 && (
              <Badge className={elevadoresSummary.com_alarmes > 0 ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>
                {elevadoresSummary.com_alarmes > 0 ? <AlertTriangle className="h-3 w-3 mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                Elevadores: {elevadoresSummary.em_servico}/{elevadoresSummary.total_elevadores} em serviço
              </Badge>
            )}
          </div>
          {lastUpdate && (
            <p className="text-xs text-gray-500 mt-1">
              Última atualização: {format(lastUpdate, "dd/MM/yyyy HH:mm:ss", { locale: pt })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => { loadWaterData(); loadOtherRealData(); }}
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <PeriodFilter 
            value={periodFilter.type === 'preset' ? periodFilter.days.toString() : 'custom'}
            onChange={setPeriodFilter}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Energia"
          value={formatNumber(energiaPeriodTotal !== null ? energiaPeriodTotal : totalStats.energia)}
          unit="kWh"
          icon={Zap}
          color="bg-amber-500"
          isRealData={energiaPeriodTotal !== null}
          subValue={energiaKpis ?
            `${energiaKpis.locais} locais | pico ${formatNumber(energiaKpis.potencia_max_kw, 1)} kW` :
            'Dados simulados'}
        />
        <StatCard
          title="Fotovoltaico"
          value={formatNumber(huaweiSummary?.total_cumulative_energy_kwh ?? totalStats.fotovoltaico)}
          unit="kWh"
          icon={Sun}
          color="bg-orange-500"
          isRealData={!!huaweiSummary && huaweiSummary.stations_count > 0}
          subValue={huaweiSummary && huaweiSummary.stations_count > 0 ?
            `${huaweiSummary.stations_online}/${huaweiSummary.stations_count} estações | ${formatNumber(huaweiSummary.total_real_time_power_kw, 1)} kW agora` :
            'Dados simulados'}
        />
        <StatCard
          title="Carregamento VE"
          value={formatNumber(totalStats.carregamento)}
          unit="kWh"
          icon={CarFront}
          color="bg-blue-500"
          subValue="Dados simulados"
        />
        <StatCard
          title="Água"
          value={waterApiConnected && waterRealData ?
            formatNumber(waterRealData.total_geral.m3, 2) :
            formatNumber(totalStats.agua)
          }
          unit="m³"
          icon={Droplets}
          color="bg-cyan-500"
          isRealData={waterApiConnected}
          subValue={waterApiConnected && waterPeriodConsumption ?
            `+${formatNumber(waterPeriodConsumption, 2)} m³ (período)` :
            undefined
          }
        />
        <StatCard
          title="Elevadores"
          value={elevadoresSummary ? formatNumber(elevadoresSummary.em_servico) : formatNumber(totalStats.elevadores)}
          unit={elevadoresSummary ? `/ ${elevadoresSummary.total_elevadores}` : 'min'}
          icon={MoveVertical}
          color="bg-slate-600"
          isRealData={!!elevadoresSummary && elevadoresSummary.total_elevadores > 0}
          subValue={elevadoresSummary && elevadoresSummary.total_elevadores > 0 ?
            `${elevadoresSummary.em_manutencao} em manutenção | ${elevadoresSummary.com_alarmes} com alarme` :
            'Dados simulados'}
        />
      </div>

      {/* Água - Dados Reais Diários */}
      {waterApiConnected && combinedDailyData && combinedDailyData.length > 0 && (
        <ChartCard title="Consumo de Água - Dados Reais (últimos dias)">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-green-100 text-green-800">
              <Wifi className="h-3 w-3 mr-1" />
              Dados em Tempo Real
            </Badge>
            <span className="text-xs text-gray-500">
              {deviceConfigs.length} contadores | {combinedDailyData.length} dias
            </span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={combinedDailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="data" 
                tick={{ fontSize: 11 }} 
                stroke="#6b7280"
                tickFormatter={(value) => format(new Date(value), 'dd/MM', { locale: pt })}
              />
              <YAxis 
                tick={{ fontSize: 12 }} 
                stroke="#6b7280" 
                tickFormatter={(value) => formatTooltipValue(value, 1)}
                unit=" m³"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar 
                dataKey="agua" 
                fill="#06b6d4" 
                name="Água (m³)" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Charts - Dados Mensais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Consumos Mensais - Linha">
          <div className="mb-2 text-xs text-gray-500 flex flex-wrap items-center gap-2">
            {Object.keys(energiaMonthlyData).length > 0 && (
              <Badge className="bg-green-100 text-green-800 text-xs"><Wifi className="h-3 w-3 mr-1" />Energia: Real</Badge>
            )}
            <span>Fotovoltaico e Carregamento: simulados nos meses sem dados reais</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={filteredMonthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} stroke="#6b7280" />
              <YAxis 
                tick={{ fontSize: 12 }} 
                stroke="#6b7280"
                tickFormatter={(value) => formatNumber(value)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line type="monotone" dataKey="energia" stroke="#f59e0b" strokeWidth={2} name="Energia (kWh)" />
              <Line type="monotone" dataKey="fotovoltaico" stroke="#f97316" strokeWidth={2} name="Fotovoltaico (kWh)" />
              <Line type="monotone" dataKey="carregamento" stroke="#3b82f6" strokeWidth={2} name="Carregamento (kWh)" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={`Recursos Mensais - Água por Dispositivo ${waterStackedChartData.length > 0 ? '(Real)' : ''}`}>
          <div className="flex items-center gap-2 mb-2">
            {waterStackedChartData.length > 0 && (
              <Badge className="bg-green-100 text-green-800 text-xs">
                <Wifi className="h-3 w-3 mr-1" />
                Água: Dados Reais
              </Badge>
            )}
            <span className="text-xs text-gray-500">
              {waterStackedChartData.length > 0 ? `${deviceNames.length} contadores | ${waterStackedChartData.length} meses` : 'Barras empilhadas por contador'}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={waterStackedChartData.length > 0 ? waterStackedChartData : filteredMonthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} stroke="#6b7280" />
              <YAxis 
                tick={{ fontSize: 12 }} 
                stroke="#6b7280"
                tickFormatter={(value) => formatTooltipValue(value, 1)}
                unit=" m³"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              {waterStackedChartData.length > 0 && deviceNames.length > 0 ? (
                // Barras empilhadas por dispositivo quando há dados reais
                deviceNames.map((name, index) => (
                  <Bar 
                    key={name}
                    dataKey={name}
                    stackId="agua"
                    fill={WATER_DEVICE_COLORS[index % WATER_DEVICE_COLORS.length]}
                    name={`${name} (m³)`}
                    radius={index === deviceNames.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                ))
              ) : (
                // Barra única quando não há dados reais ou sem dispositivos
                <Bar 
                  dataKey="agua" 
                  fill="#06b6d4" 
                  name="Água (m³)" 
                  radius={[4, 4, 0, 0]}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Combined View */}
      <ChartCard title="Visão Geral de Todas as Categorias">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {combinedAllCategoriesData.length > 0 && (
            <Badge className="bg-green-100 text-green-800 text-xs">
              <Wifi className="h-3 w-3 mr-1" />
              Água: Real
            </Badge>
          )}
          {Object.keys(energiaMonthlyData).length > 0 && (
            <Badge className="bg-green-100 text-green-800 text-xs">
              <Wifi className="h-3 w-3 mr-1" />
              Energia: Real
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">
            Fotovoltaico, Carregamento e Elevadores: simulados nesta vista mensal
          </Badge>
          {combinedAllCategoriesData.length > 0 && (
            <span className="text-xs text-gray-500">
              | Período: {combinedAllCategoriesData.length} meses
            </span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={combinedAllCategoriesData.length > 0 ? combinedAllCategoriesData : filteredMonthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="mes" tick={{ fontSize: 12 }} stroke="#6b7280" />
            <YAxis 
              tick={{ fontSize: 12 }} 
              stroke="#6b7280"
              tickFormatter={(value) => formatNumber(value)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Bar dataKey="energia" fill="#f59e0b" name="Energia (kWh)" radius={[2, 2, 0, 0]} />
            <Bar dataKey="fotovoltaico" fill="#f97316" name="Fotovoltaico (kWh)" radius={[2, 2, 0, 0]} />
            <Bar dataKey="carregamento" fill="#3b82f6" name="Carregamento (kWh)" radius={[2, 2, 0, 0]} />
            <Bar dataKey="agua" fill="#06b6d4" name="Água (m³)" radius={[2, 2, 0, 0]} />
            <Bar dataKey="elevadores" fill="#475569" name="Elevadores (min)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
};

export default Dashboard;
